import { createContext, FC, ReactNode, useContext, useEffect, useRef } from 'react';
import { createStore, useStore } from 'zustand';
import { Storage } from '@capacitor/storage';
import { requestLocationFromBrowser } from './requestLocationFromBrowser';

export enum LocationPermissionType {
  NotGranted,
  Denied,
  Granted
}

export enum LocationState {
  Unknown = 'Unknown',
  Loaded = 'Loaded',
  Error = 'Error'
}

export interface DeviceLocationService {
  location: {
    state: LocationState;
    permission?: LocationPermissionType;
    position?: GeolocationPosition;
    dateGranted?: string;
    error?: unknown;
  };
  restoreLocationPermissions: () => Promise<void>;
  validateAndSetLocationPermission: (newPermission: LocationPermissionType) => Promise<void>;
  resetLocationPermission: () => Promise<void>;
  getUserLocation: () => Promise<void>;
}

type DeviceLocationStore = ReturnType<typeof makeStore>;

const disableSetLocationPermission = (storedValue: string | null) => {
  //Returns true if *invalid*
  //Returns false if *valid*
  if (storedValue !== null && storedValue !== LocationPermissionType.NotGranted.toString()) return true;
  return false;
};

const makeStore = () =>
  createStore<DeviceLocationService>()((set, get) => ({
    location: {
      permission: undefined,
      state: LocationState.Unknown,
      dateGranted: undefined
    },
    restoreLocationPermissions: async () => {
      const { value: storedValue } = await Storage.get({ key: 'locationPermission' });
      const { value: storedDate } = await Storage.get({ key: 'dateLocationPermissionGranted' });
      set(state => ({
        location: {
          ...state.location,
          permission: parseInt(storedValue || LocationPermissionType.NotGranted.toString()),
          dateGranted: storedDate === null ? undefined : storedDate
        }
      }));
      if (get().location.permission === LocationPermissionType.Granted && !get().location.position) {
        get().getUserLocation();
      }
    },
    resetLocationPermission: async () => {
      await Storage.remove({ key: 'locationPermission' });
      set(state => ({
        location: {
          ...state.location,
          permission: LocationPermissionType.NotGranted,
          dateGranted: undefined
        }
      }));
    },
    validateAndSetLocationPermission: async (newPermission: LocationPermissionType) => {
      const { value: storedValue } = await Storage.get({ key: 'locationPermission' });
      if (disableSetLocationPermission(storedValue)) return;
      await Storage.set({
        key: 'locationPermission',
        value: newPermission.toString()
      });
      set(state => ({
        location: {
          ...state.location,
          permission: newPermission
        }
      }));
    },
    getUserLocation: async () => {
      try {
        const date = new Date().toISOString();
        const position = await requestLocationFromBrowser();

        set(state => ({
          location: {
            ...state.location,
            state: LocationState.Loaded,
            position,
            permission: LocationPermissionType.Granted,
            dateGranted: date
          }
        }));
        await Storage.set({ key: 'dateLocationPermissionGranted', value: date });
      } catch (error) {
        await Storage.remove({ key: 'dateLocationPermissionGranted' });
        set(state => ({
          location: {
            ...state.location,
            state: LocationState.Error,
            error,
            permission: LocationPermissionType.Denied,
            dateGranted: undefined
          }
        }));
      }
    }
  }));

const DeviceLocationContext = createContext<DeviceLocationStore | null>(null);

export const DeviceLocationProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const storeRef = useRef<DeviceLocationStore | null>(null);

  if (!storeRef.current) {
    storeRef.current = makeStore();
  }

  // Handle the initial state restoration that used to happen outside the React lifecycle
  useEffect(() => {
    if (storeRef.current) {
      storeRef.current.getState().restoreLocationPermissions();
    }
  }, []);

  return <DeviceLocationContext.Provider value={storeRef.current}>{children}</DeviceLocationContext.Provider>;
};

export const useDeviceLocation = <T,>(selector: (state: DeviceLocationService) => T): T => {
  const store = useContext(DeviceLocationContext);
  if (!store) {
    throw new Error('useDeviceLocation must be used within a DeviceLocationProvider');
  }
  return useStore(store, selector);
};
